// Nomad-mon Go! — v11 FUSION RIM: the extravagant animated border every Fusion
// card wears in WebGL (pack, forge, squad, battle). One additive plane behind
// the card: a flowing border band, energy filaments streaming off the edge,
// ember/plasma motes drifting outward and four rotating sigil corners. The
// palette climbs with the Fusion's tier: ember gold (1-8), plasma (9-16),
// full prismatic white-hot (17-24). It shares the card foil's uTime uniform
// object, so every surface that already animates the foil animates the rim.
//
// FORGE PRISM (battle): uPrism 0..1 repaints every band in the Fusion FORGE
// palette (the prismatic conic of forged-frame.js .b2 / forged-fx.js hue()):
// the hue flows round the border, and uAura adds a soft rainbow halo outside
// it (the high-tier extra; 0 on the low tier). uDim fades the whole rim, so an
// attack rim can take the border over and hand it back.
import * as THREE from 'three';

const frag = /* glsl */`
  precision highp float;
  varying vec2 vP;
  uniform float uTime, uBand, uDim, uPrism, uAura;
  uniform vec2 uHalf;
  uniform float uR;
  float sdRound(vec2 p, vec2 b, float r) { vec2 q = abs(p) - b + r; return length(max(q, 0.0)) + min(max(q.x, q.y), 0.0) - r; }
  float hash(vec2 p) { return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }
  vec3 spectral(float t) { return 0.5 + 0.5 * cos(6.28318 * (t + vec3(0.0, 0.33, 0.67))); }
  // the forge's prismatic wheel (pure hues, a touch of pink lift like its conic)
  vec3 forgeHue(float h) { vec3 c = clamp(abs(fract(h + vec3(0.0, 0.6667, 0.3333)) * 6.0 - 3.0) - 1.0, 0.0, 1.0); return mix(c, vec3(1.0, 0.45, 0.8), 0.08); }
  vec3 palette(float a, float t) {
    vec3 ember = mix(vec3(1.0, 0.42, 0.08), vec3(1.0, 0.86, 0.45), 0.5 + 0.5 * sin(a * 3.0 + t * 1.3));
    vec3 plasma = mix(vec3(0.55, 0.25, 1.0), vec3(0.15, 0.95, 1.0), 0.5 + 0.5 * sin(a * 2.0 - t * 1.1));
    vec3 prism = mix(spectral(a / 6.28318 + t * 0.18), vec3(1.0), 0.18);
    return uBand < 0.5 ? ember : uBand < 1.5 ? plasma : prism;
  }
  void main() {
    vec2 p = vP;
    float d = sdRound(p, uHalf, uR);           // > 0 outside the card
    float a = atan(p.y, p.x);
    float t = uTime;
    vec3 col = palette(a, t);
    vec3 fh = forgeHue(a / 6.28318 + t * 0.16);
    col = mix(col, fh, uPrism);
    float out0 = step(0.0, d);
    // the flowing border band hugging the card edge
    float band = exp(-max(d, 0.0) * 34.0) * smoothstep(-0.012, 0.004, d);
    float flow = 0.65 + 0.35 * sin(a * 9.0 - t * 3.2) * sin(a * 5.0 + t * 1.7);
    float energy = band * flow * (1.3 + 0.35 * uBand);
    // energy filaments streaming outward
    float fil = pow(abs(sin(a * 38.0 + 2.5 * sin(a * 7.0 + t * 0.9) + t * 2.2)), 26.0);
    fil *= exp(-d * (16.0 - 3.0 * uBand)) * out0 * (0.55 + 0.45 * sin(t * 6.0 + a * 11.0));
    energy += fil * (0.9 + 0.5 * uBand);
    // ember / plasma motes drifting off the edge
    vec2 g = vec2(a * 18.0, d * 26.0 - t * (1.4 + 0.5 * uBand));
    vec2 cell = floor(g);
    vec2 f = fract(g) - 0.5;
    float h = hash(cell);
    float mote = smoothstep(0.16, 0.0, length(f - (vec2(hash(cell + 3.1), hash(cell + 7.7)) - 0.5) * 0.6));
    mote *= step(0.72, h) * out0 * exp(-d * 9.0) * (0.6 + 0.4 * sin(t * 9.0 + h * 40.0));
    energy += mote * 1.6;
    // four rotating sigil corners
    for (int i = 0; i < 4; i++) {
      float sx = (i == 0 || i == 3) ? 1.0 : -1.0;
      float sy = (i < 2) ? 1.0 : -1.0;
      vec2 c = vec2(sx * (uHalf.x + 0.035), sy * (uHalf.y + 0.035));
      vec2 q = p - c;
      float r = length(q);
      float ang = atan(q.y, q.x) + t * (i == 0 || i == 2 ? 1.1 : -1.1);
      float ring = smoothstep(0.007, 0.0, abs(r - 0.078)) + 0.6 * smoothstep(0.005, 0.0, abs(r - 0.052));
      float runes = smoothstep(0.55, 1.0, sin(ang * 6.0)) * smoothstep(0.012, 0.0, abs(r - 0.065));
      float spokes = smoothstep(0.92, 1.0, cos(ang * 3.0)) * step(r, 0.05);
      energy += (ring * 0.9 + runes * 1.4 + spokes * 0.8) * step(r, 0.1) * (1.0 + 0.3 * uBand);
    }
    // FORGE PRISM: a bold saturated rainbow line hugging the border, the band's
    // energy re-coloured, and (high tier) a soft rainbow halo breathing outside
    // it with its hue offset so the two shimmer against each other. Saturated
    // and held under the bloom knee: on the field it must read as COLOUR, not
    // as white light.
    vec3 outc = col * energy;
    if (uPrism > 0.0) {
      vec3 pc = fh * fh; pc /= max(max(pc.r, pc.g), max(pc.b, 0.001));
      float line = exp(-abs(d - 0.012) * 60.0) * smoothstep(-0.02, 0.0, d) * (0.8 + 0.2 * sin(a * 24.0 - t * 6.0));
      float aura = out0 * exp(-d * 11.0) * smoothstep(0.17, 0.1, d) * (0.6 + 0.4 * sin(a * 3.0 - t * 1.9));
      vec3 ac = forgeHue(a / 6.28318 - t * 0.11 + 0.33); ac = ac * ac; ac /= max(max(ac.r, ac.g), max(ac.b, 0.001));
      vec3 pr = pc * (line * 0.9 + min(energy, 1.6) * 0.32) + ac * aura * 0.55 * uAura;
      outc = mix(outc, min(pr, vec3(0.92)), uPrism);
    }
    gl_FragColor = vec4(outc * uDim, 1.0);
  }
`;
const vert = /* glsl */`
  varying vec2 vP;
  void main() { vP = position.xy; gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }
`;

export function fusionBand(tier) { return tier >= 17 ? 2 : tier >= 9 ? 1 : 0; }

export function makeFusionRim({ CW, CH, CR, tier = 1, uTime, z = -0.05 }) {
  const pad = 0.34;
  const mat = new THREE.ShaderMaterial({
    uniforms: {
      uTime: uTime || { value: 0 },
      uBand: { value: fusionBand(tier) },
      uDim: { value: 1 },
      uPrism: { value: 0 },
      uAura: { value: 0 },
      uHalf: { value: new THREE.Vector2(CW / 2, CH / 2) },
      uR: { value: CR },
    },
    vertexShader: vert, fragmentShader: frag,
    transparent: true, depthWrite: false, blending: THREE.AdditiveBlending,
  });
  const mesh = new THREE.Mesh(new THREE.PlaneGeometry(CW + pad, CH + pad), mat);
  mesh.position.z = z;
  mesh.renderOrder = -1;
  mesh.name = 'fusionRim';
  return mesh;
}
